import { Order } from "@/types/ApiTypes";
import { IoClose } from "react-icons/io5";
import DriverRouteMap from "./DriverRouteMap";
import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction } from "react";
import { AppConfig } from "@/utils/AppConfig";
import { formatMoney } from "@/utils/helpers";


interface DriverModalProps {
  order: Order;
  toggleModal: () => void;
  setDriverPoint: Dispatch<SetStateAction<google.maps.LatLngLiteral | null>>
  driverPoint: google.maps.LatLngLiteral | null
}

export default function DriverModal({ order, toggleModal, driverPoint, setDriverPoint }: DriverModalProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const lat = order.address?.lat;
  const lng = order.address?.lng;

  const userPoint = lat && lng ? { lat, lng } : null;




  return (
    <div
      onClick={(e) => {
        toggleModal();
        e.stopPropagation()
      }}
      className="fixed inset-0 z-[9999999] bg-black bg-opacity-35 flex items-center justify-center p-4 sm:p-6"
    >
      <div
        className="relative w-full max-w-3xl max-h-[98vh] overflow-y-auto space-y-2 bg-white rounded-lg shadow-lg p-4 "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <IoClose
          size={30}
          onClick={toggleModal}
          className=" ms-auto cursor-pointer  text-red-500"
        />

        {/* Map Section */}
        {userPoint && driverPoint ? (
          <div className="w-full h-[300px] sm:h-[300px] rounded overflow-hidden mb-4">
            <DriverRouteMap
              setDriverPoint={setDriverPoint}
              userPoint={userPoint}
              driverPoint={driverPoint}

            />

          </div>
        ) : (
          <p className="text-center text-gray-500 mt-10">
            No customer location available.
          </p>
        )}
        <div className="flex justify-end mb-2">
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`}
            target="_blank"
            rel="noopener noreferrer"
            className="my-btn text-center"
          >
            {t("address.navigateWithGoogleMaps") || "Navigate with Google Maps"}
          </a>

        </div>
        <div className="flex items-center border rounded-lg p-2 justify-center space-x-2">
          <span>
            {order?.items?.map((item, index) => {
              const moreThanOne = item.quantity > 1;

              return (
                <div
                  key={index}
                  className="flex flex-col items-center gap-1 text-center"
                >
                  <div className="flex items-center justify-between gap-10">
                    <span
                      className="flex flex-row-reverse gap-5 whitespace-normal"
                      dir="rtl"
                    >
                      <span dir="ltr"> {item.quantity} X </span>
                      <span>
                        <span className="flex justify-center">{item.nameEN}</span>
                        <span className="flex justify-center">
                          {item.nameAR !== item.nameEN ? item.nameAR : ""}
                        </span>
                      </span>
                    </span>
                  </div>

                  {item.addons?.length > 0 && (
                    <span className="flex flex-col items-center font-bold text-center">
                      {t("ordersBody.Addons")}
                      <span className="flex flex-col font-normal text-sm">
                        {item.addons.map((addon, i) => (
                          <span key={i}>
                            {locale === "en" ? addon.nameEN : addon.nameAR}
                          </span>
                        ))}
                      </span>
                    </span>
                  )}

                  {item.options?.length > 0 && (
                    <span className="flex flex-col items-center font-bold text-center">
                      {t("ordersBody.Options")}
                      <span className="flex flex-col font-normal text-sm">
                        {item.options.flatMap((option) =>
                          option.options.map((optItem, i) => (
                            <span key={i}>
                              {locale === "en" ? optItem.nameEN : optItem.nameAR}
                            </span>
                          )),
                        )}
                      </span>
                    </span>
                  )}
                  {moreThanOne && <div className="border-b border-b-black w-full"></div>}

                </div>
              );
            })}
          </span>
        </div>
        <div className="w-full rounded-md border p-4 flex justify-between shadow-sm ">
          <div>
            <h2>{t("ordersBody.Total")}</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className={`${order?.paid === true ? "text-green-600" : "text-red-600"}`}>{order?.paid === true ? t("ordersBody.Paid") : t("ordersBody.Unpaid")}</span>
            <span dir="rtl" className={`${order?.paid === true ? "text-green-600" : "text-red-600"} flex items-center gap-1`}>{formatMoney(order?.total)}
              {AppConfig.currency.icon()}</span>

          </div>
        </div>
        {/* Address Section */}
        {order.address && (
          <div className="w-full rounded-md border p-4 shadow-sm  text-right">
            <h3 className="text-base font-semibold text-gray-700 mb-4 border-b pb-2">
              {t("address.deliveryAddress")}
            </h3>
            <div className="space-y-2 text-sm text-gray-800">
              <div className="flex justify-between">
                <span className="text-gray-600">{t("address.city")} :</span>
                <span>{order.address?.city || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("address.street")} :</span>
                <span>{order.address?.street || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("address.building")} :</span>
                <span>{order.address?.buildingNumber || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("address.floor")} :</span>
                <span>{order.address?.floorNumber || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("address.apartment")} :</span>
                <span>{order.address?.apartmentNumber || "-"}</span>
              </div>
            </div>

            {order.address?.additionalInfo && (
              <div className="mt-3 text-xs  text-gray-500 text-center">
                <h1 className="font-bold mb-2">  {t("address.info")}</h1>
                {order.address.additionalInfo}
              </div>
            )}
          </div>
        )}
      </div>
    </div>


  );
}
